import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/motion";
import Testimonials from "@/components/Testimonials";

const IMG = {
  mist: "https://images.unsplash.com/photo-1680790121109-382659f05770?q=80&w=2000&auto=format&fit=crop",
  forest:
    "https://images.unsplash.com/photo-1425913397330-cf8af2ff40a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxmb3Jlc3QlMjBwYXRoJTIwc3VubGlnaHQlMjB0cmVlc3xlbnwwfHx8fDE3ODc5MTE4NDF8MA&ixlib=rb-4.1.0&q=85",
  individual:
    "https://images.unsplash.com/photo-1644587590554-7ad526ae060d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxjYW5kaWQlMjBwb3J0cmFpdCUyMHRob3VnaHRmdWwlMjBwZXJzb258ZW58MHx8fHwxNzg4MTg3OTI4fDA&ixlib=rb-4.1.0&q=85",
  leader:
    "https://images.pexels.com/photos/39257818/pexels-photo-39257818.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  bokeh:
    "https://images.unsplash.com/photo-1608483555724-d430b692b776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHx3YXJtJTIwYWJzdHJhY3QlMjBsaWdodHxlbnwwfHx8fDE3ODYzNTYzMzR8MA&ixlib=rb-4.1.0&q=85",
  about:
    "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=1200&auto=format&fit=crop",
};

const credentials = [
  "Maxwell Leadership Certified Trainer",
  "HRDF Train-the-Trainer (TTT) Certified",
  "Certified Associate Coach (CAC) by CCA",
  "Masters in Ministry (MBS)",
  "BSc Management & Psychology (KY)",
];

const dimensions = [
  "Who I am", "What I value", "What I believe", "What I have learned",
  "What I need to let go of", "What I need to learn", "My strengths",
  "My limiting beliefs", "My relationships", "My habits", "My boundaries",
  "My purpose", "My next chapter", "My contribution", "My commitments",
  "My people", "My next courageous step",
];

const serve = [
  {
    title: "Individuals",
    copy: "For people navigating their own messy middle — loss, disruption, transition, burnout, or the quiet ache of not knowing what comes next.",
    img: "https://images.unsplash.com/photo-1508672019048-805c876b67e2?q=80&w=1200&auto=format&fit=crop",
    alt: "A person sitting quietly at the end of a wooden dock, facing a still lake and mountains",
  },
  {
    title: "Leaders",
    copy: "For people who want to lead themselves first, so they can serve and grow others with intentionality, courage and care.",
    img: "/alex-city-window.jpg",
    alt: "Alex in silhouette, looking out over the city through a rain-streaked window",
  },
  {
    title: "Organisations",
    copy: "Projects don't grow organisations. Growing people do. Coaching, training and facilitation for the humans behind the outcomes.",
    img: "/alex-workshop.jpg",
    alt: "Alex facilitating a room of people in a warm workshop space",
  },
];

const howWords = ["Listen", "Reflect", "Challenge", "Equip", "Encourage", "Account", "Grow"];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    document.title = "People Growing People — You don't have to go through the messy middle alone";
  }, []);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden bg-ink">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img
            src={IMG.mist}
            alt="A still dock reaching into a misty lake at dawn"
            className="w-full h-[120%] object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32 pt-40 w-full">
          <MaskedLine delay={0.15}>
            <span className="text-xs uppercase tracking-[0.3em] text-paper/60 font-sans">
              People Growing People
            </span>
          </MaskedLine>
          <h1 className="font-serif text-paper text-[11vw] sm:text-6xl md:text-7xl lg:text-[6.5rem] leading-[1.02] tracking-tight mt-6 max-w-5xl">
            <MaskedLine delay={0.3}>You don't have to</MaskedLine>
            <MaskedLine delay={0.4}>go through the</MaskedLine>
            <MaskedLine delay={0.5}>
              <span className="italic text-clay">messy middle</span>
            </MaskedLine>
            <MaskedLine delay={0.6}>alone.</MaskedLine>
          </h1>
          <MaskedLine delay={0.78} className="mt-10 max-w-xl">
            <span className="text-paper/80 text-base md:text-lg leading-relaxed font-sans">
              When life doesn't go according to plan, you may not need another
              quick fix. You may need space to pause, people to walk with you,
              practical tools to move forward, and hope for what is not yet visible.
            </span>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 flex flex-wrap gap-4"
          >
            <Link
              to="/work-with-alex"
              data-testid="hero-cta-work-with-alex"
              className="group inline-flex items-center gap-3 bg-clay text-paper text-xs uppercase tracking-[0.22em] px-8 py-4 hover:bg-paper hover:text-ink transition-colors duration-300"
            >
              Work With Alex
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/messy-middle"
              data-testid="hero-cta-explore-messy-middle"
              className="inline-flex items-center gap-3 border border-paper/40 text-paper text-xs uppercase tracking-[0.22em] px-8 py-4 hover:bg-paper hover:text-ink transition-colors duration-300"
            >
              Explore The Messy Middle
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 right-6 md:right-10 z-10 hidden md:flex items-center gap-3 text-paper/50 text-xs uppercase tracking-[0.25em]"
        >
          Scroll <ArrowDown className="w-3.5 h-3.5 animate-bounce" />
        </motion.div>
      </section>

      {/* THE MESSY MIDDLE EXPLAINER */}
      <section data-testid="section-messy-middle" className="border-t border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40 grid md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-5">
            <div className="md:sticky md:top-32">
              <Reveal>
                <Overline>01 — The Season</Overline>
                <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-none mt-6">
                  The Messy <span className="italic text-moss">Middle</span>
                </h2>
              </Reveal>
            </div>
          </div>
          <div className="md:col-span-7 md:pl-12 md:border-l md:border-line">
            <Reveal>
              <p className="font-serif text-2xl md:text-4xl leading-snug tracking-tight text-ink/90">
                Failures. <span className="italic">Fixtures.</span> FOMOs.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-10 text-base md:text-lg leading-relaxed text-ink/75 max-w-xl">
                The messy middle is the in-between season — where life has been
                disrupted, but the next chapter has not yet become clear. Chaos,
                confusion, loss, disappointment, uncertainty. A lack of visible
                outcomes.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-ink/75 max-w-xl">
                But here is what the messy middle hides: beneath what you cannot
                see, significant growth may already be taking place. The messy
                parts of your story are not necessarily disqualifiers. They can
                become catalysts — for insight, courage and contribution.
              </p>
            </Reveal>
            <Reveal delay={0.26}>
              <Link
                to="/messy-middle"
                data-testid="section-messy-middle-link"
                className="group mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-moss border-b border-moss/40 pb-1 hover:text-clay hover:border-clay transition-colors duration-300"
              >
                Step inside the journey
                <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHAT IF THIS IS NOT THE END — dark inverted */}
      <section data-testid="section-reframe" className="bg-ink text-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-44">
          <Reveal>
            <Overline className="text-paper/50">02 — The Reframe</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-5xl sm:text-6xl md:text-8xl tracking-tight leading-[0.95] mt-8 max-w-5xl">
              What if this is <span className="italic text-clay">not the end?</span>
            </h2>
          </Reveal>
          <div className="mt-16 md:mt-24 grid md:grid-cols-4 gap-px bg-paper/15 border border-paper/15">
            {["Reframe", "Repurpose", "Pivot", "Grow"].map((w, i) => (
              <Reveal key={w} delay={i * 0.08} className="bg-ink">
                <div className="p-8 md:p-10">
                  <p className="font-serif text-4xl md:text-5xl text-paper/20">
                    0{i + 1}
                  </p>
                  <p className="font-serif text-2xl md:text-3xl mt-6 tracking-tight">{w}</p>
                  <p className="text-sm text-paper/60 mt-4 leading-relaxed">
                    {[
                      "See the season differently — pain can become sacred ground for growth.",
                      "Nothing wasted. Disruption becomes material for meaning.",
                      "Turn, deliberately, toward what is still possible.",
                      "Better, not bitter. A return on failure is possible.",
                    ][i]}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VERSION 7.0 */}
      <section data-testid="section-version-7" className="border-t border-line bg-sand/40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-7">
              <Reveal>
                <Overline>03 — The Becoming</Overline>
                <h2 className="font-serif text-5xl sm:text-6xl md:text-7xl tracking-tight leading-[0.95] mt-6">
                  Version <span className="italic text-clay">7.0</span>
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-10 text-base md:text-lg leading-relaxed text-ink/75 max-w-xl">
                  Not a final, perfected you. Seven speaks of completion, rest,
                  renewal — and beginning again. Version 7.0 is about{" "}
                  <em className="font-serif text-xl md:text-2xl text-ink">
                    becoming rather than arriving.
                  </em>
                </p>
              </Reveal>
              <Reveal delay={0.18}>
                <div className="mt-12 space-y-0 border-t border-line">
                  {[
                    ["I", "Pause long enough to tell the truth about where you are."],
                    ["II", "Name what is still working, and the strengths pain has buried."],
                    ["III", "Take the next courageous step — and help someone else take theirs."],
                  ].map(([n, t], i) => (
                    <div key={n} className="flex gap-8 py-8 border-b border-line items-baseline">
                      <span className="font-serif text-2xl text-clay shrink-0 w-10">{n}.</span>
                      <p className="text-base md:text-lg text-ink/80 leading-relaxed">{t}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.24}>
                <Link
                  to="/messy-middle"
                  data-testid="cta-discover-version-7"
                  className="group mt-12 inline-flex items-center gap-3 bg-moss text-paper text-xs uppercase tracking-[0.22em] px-8 py-4 hover:bg-ink transition-colors duration-300"
                >
                  Discover Version 7.0
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </Reveal>
            </div>
            <div className="md:col-span-5 md:pt-24">
              <Reveal delay={0.15}>
                <div className="border border-line bg-paper p-8 md:p-10">
                  <p className="text-xs uppercase tracking-[0.25em] text-stone mb-8">
                    The dimensions of becoming
                  </p>
                  <ul className="columns-2 gap-8 space-y-3">
                    {dimensions.map((d) => (
                      <li key={d} className="text-sm text-ink/75 leading-snug break-inside-avoid border-l-2 border-clay/40 pl-3">
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* HOW I WORK — MARQUEE */}
      <section data-testid="section-how-i-work" className="border-t border-line overflow-hidden">
        <div className="py-20 md:py-28 border-b border-line">
          <Marquee speed={28} gradient={false} data-testid="how-marquee">
            {howWords.map((w) => (
              <span key={w} className="font-serif text-5xl md:text-7xl tracking-tight text-ink/85 mx-8 flex items-center gap-8">
                {w} <span className="text-clay text-3xl md:text-4xl">·</span>
              </span>
            ))}
          </Marquee>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <Reveal>
              <Overline>04 — How I Work</Overline>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none mt-6">
                Safe, but <span className="italic text-moss">not passive.</span>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-2 gap-x-12 gap-y-10">
            {[
              ["Compassionate but honest", "I listen for repeated words, buried strengths, and the transformation that may already be happening — and I challenge what keeps you stuck."],
              ["Hopeful but realistic", "Not quick fixes. Practical handles — grounding methods, journaling, honest reflection — that you can actually use in real life."],
              ["Challenging but life-giving", "Rumination, self-limiting beliefs and habits that hinder transformation get named — with warmth, not judgement."],
              ["People need people", "Sometimes you need someone to believe in you before you can believe in yourself again. That is where we start."],
            ].map(([t, c], i) => (
              <Reveal key={t} delay={i * 0.08}>
                <div className="border-t-2 border-ink pt-6">
                  <h3 className="font-serif text-2xl tracking-tight">{t}</h3>
                  <p className="mt-4 text-sm md:text-base text-ink/70 leading-relaxed">{c}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHO I SERVE */}
      <section data-testid="section-who-i-serve" className="border-t border-line bg-sand/40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
          <Reveal>
            <Overline>05 — Who I Serve</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-none mt-6 max-w-3xl">
              Stuck somewhere between <span className="italic text-clay">what was</span> and{" "}
              <span className="italic text-moss">what comes next.</span>
            </h2>
          </Reveal>
          <div className="mt-16 md:mt-24 grid md:grid-cols-3 gap-8 md:gap-10">
            {serve.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <Link to="/contact" data-testid={`serve-card-${s.title.toLowerCase()}`} className="group block">
                  <div className="overflow-hidden border border-line">
                    <img
                      src={s.img}
                      alt={s.alt}
                      loading="lazy"
                      className="w-full aspect-[4/5] object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="pt-6 flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-serif text-2xl md:text-3xl tracking-tight">{s.title}</h3>
                      <p className="mt-3 text-sm md:text-base text-ink/70 leading-relaxed">{s.copy}</p>
                    </div>
                    <ArrowUpRight className="w-5 h-5 mt-1 shrink-0 text-clay transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section data-testid="section-philosophy" className="border-t border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-8 md:col-start-3 text-left">
            <Reveal>
              <Overline>06 — The Philosophy</Overline>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="font-serif text-3xl sm:text-4xl md:text-6xl tracking-tight leading-[1.1] mt-8">
                People grow best with a safe environment, the right people around
                them, practical tools, honest reflection, accountability —{" "}
                <span className="italic text-clay">and hope.</span>
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-10 text-base md:text-lg leading-relaxed text-ink/70 max-w-2xl">
                Transformation becomes sustainable when it happens in community.
                When one person grows, their family feels it. Then their team,
                their community, their organisation — and the generations after
                them. That is the deeper meaning of People Growing People.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ABOUT ALEX TEASER */}
      <section data-testid="section-about-alex" className="border-t border-line bg-sand/40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <Reveal>
              <div className="overflow-hidden border border-line">
                <img
                  src="/alex.jpg"
                  alt="Alex — coach, trainer and facilitator"
                  loading="lazy"
                  className="w-full aspect-[3/4] object-cover"
                />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-7 md:pl-8">
            <Reveal>
              <Overline>07 — About Alex</Overline>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none mt-6">
                I've lived through some messy middles.{" "}
                <span className="italic text-moss">I'm still becoming.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-8 text-base md:text-lg leading-relaxed text-ink/75 max-w-xl">
                Alex is a coach, trainer and facilitator who has known disruption
                personally — loss, failure, quiet seasons, and the slow work of
                repair. He doesn't coach from arrival. He walks with people from
                the middle, gathering tools, telling the truth, and believing in
                them before they can believe in themselves again.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 font-serif italic text-xl md:text-2xl text-clay">
                "Let me walk with you through yours."
              </p>
            </Reveal>
            <Reveal delay={0.26}>
              <Link
                to="/about"
                data-testid="cta-meet-alex"
                className="group mt-10 inline-flex items-center gap-3 border border-ink text-ink text-xs uppercase tracking-[0.22em] px-8 py-4 hover:bg-ink hover:text-paper transition-colors duration-300"
              >
                Meet Alex
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CREDENTIALS */}
      <section data-testid="section-credentials" className="border-t border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight leading-tight max-w-4xl">
              Upskilled to truly grow people.{" "}
              <span className="italic text-moss">Committed to keep growing.</span>
            </h2>
          </Reveal>
          <div className="mt-14 border-t border-line">
            {credentials.map((c, i) => (
              <Reveal key={c} delay={i * 0.05}>
                <div
                  data-testid={`credential-${i + 1}`}
                  className="flex items-baseline gap-6 md:gap-12 py-6 border-b border-line"
                >
                  <span className="font-serif text-lg text-clay shrink-0">
                    0{i + 1}
                  </span>
                  <p className="font-serif text-xl md:text-3xl tracking-tight text-ink/85">
                    {c}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* KIND WORDS */}
      <Testimonials />

      {/* THE NOT YET — CLOSING */}
      <section data-testid="section-not-yet" className="relative overflow-hidden bg-ink text-paper">
        <img
          src="/alex-sunset.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-transparent to-ink/80" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 py-32 md:py-48">
          <Reveal>
            <Overline className="text-paper/50">08 — The Not Yet</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-5xl sm:text-6xl md:text-8xl tracking-tight leading-[0.95] mt-8 max-w-5xl">
              Your story is <span className="italic text-clay">still being written.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-10 text-base md:text-lg leading-relaxed text-paper/75 max-w-xl">
              We cannot always see what is coming next. But we can choose how we
              live today. The Not Yet can be a source of hope rather than
              frustration — and maybe, today, the next step is simply a
              conversation.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <Link
              to="/contact"
              data-testid="cta-next-courageous-step"
              className="group mt-12 inline-flex items-center gap-3 bg-clay text-paper text-xs uppercase tracking-[0.22em] px-10 py-5 hover:bg-paper hover:text-ink transition-colors duration-300"
            >
              Take Your Next Courageous Step
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
